import { useNavigate } from "react-router-dom";
import { useApi } from "../../../providers/ApiProvider";
import DatePickerField from "../../../components/form/date-picker";
import InputField from "../../../components/form/input-field";
import Button from "../../../components/form/button";
import TemplateEditor from "../../../components/template-editor";
import { useEffect, useState } from "react";
import { MarketingCampaignProps } from "../../../types/MarketingCampaignProps";
import { Eye, Maximize2, Pencil, Send, X } from "lucide-react";
import DefaultModal from "../../../components/modal/default-modal";
import { useSingleMarketingCampaign } from "../../../composables/marketing/useSingleMarketingCampaign";

const MarketingCampaignForm = ({
  marketingCampaign,
}: {
  marketingCampaign?: MarketingCampaignProps;
}) => {
  const api = useApi();
  const navigate = useNavigate();

  type Tab = "edit" | "preview" | "test";

  const tabs: { key: Tab; label: string; Icon: any; hidden?: boolean }[] = [
    { key: "edit", label: "Edit", Icon: Pencil },
    { key: "preview", label: "Preview", Icon: Eye },
    { key: "test", label: "Test", Icon: Send, hidden: !marketingCampaign },
  ];

  const [activeTab, setActiveTab] = useState<Tab>("edit");
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const [campaignName, setCampaignName] = useState<string>("");
  const [campaignSubject, setCampaignSubject] = useState<string>("");
  const [campaignPreheader, setCampaignPreheader] = useState<string>("");
  const [scheduledDate, setScheduledDate] = useState<string>("");
  const [scheduledTime, setScheduledTime] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [testToEmail, setTestToEmail] = useState<string>("");

  useEffect(() => {
    if (marketingCampaign) {
      setCampaignName(marketingCampaign.title);
      setCampaignSubject(marketingCampaign.subject);
      setCampaignPreheader(marketingCampaign.preheader || "");
      setContent(marketingCampaign.content);
      const scheduledAt = marketingCampaign.scheduled_at;
      if (scheduledAt) {
        const date = new Date(scheduledAt);
        setScheduledDate(date.toISOString().split("T")[0]);
        setScheduledTime(date.toTimeString().split(" ")[0].slice(0, 5));
      }else{
        setScheduledDate("");
        setScheduledTime("");
      }
    }
  }, [marketingCampaign]);

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const request = await api.POST({
      endpoint: "/marketing-campaigns" + (marketingCampaign ? `/update` : ""),
      payload: {
        title: campaignName,
        subject: campaignSubject,
        preheader: campaignPreheader,
        content: content,
        scheduled_date: scheduledDate || undefined,
        scheduled_time: scheduledTime || undefined,
        uuid: marketingCampaign?.uuid || undefined,
      },
      refreshKeys: ["marketing-campaigns"],
    });

    if (!request) return;

    if (!marketingCampaign) {
      navigate(`/marketing/${request.uuid}`);
    }
  };

  const publish = async () => {
    const request = await api.POST({
      endpoint: "/marketing-campaigns/publish",
      payload: {
        title: campaignName,
        subject: campaignSubject,
        preheader: campaignPreheader,
        content: content,
        scheduled_date: scheduledDate || undefined,
        scheduled_time: scheduledTime || undefined,
        uuid: marketingCampaign?.uuid,
      },
      refreshKeys: ["marketing-campaigns"],
    });

    if (!request) return;

    navigate(`/marketing/${request.data.uuid}`);
  };

  const sendTest = async () => {
    if (!marketingCampaign?.uuid) return;

    const res = await api.POST({
      endpoint: `/marketing-campaigns/send-test-email`,
      payload: {
        to_email: testToEmail,
        campaign_uuid: marketingCampaign.uuid,
      },
    });

    if (!res) return;

    setTestToEmail("");
  };

  // Close fullscreen preview with Escape, and lock page scroll while it's open.
  useEffect(() => {
    if (!isFullscreen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isFullscreen]);

  return (
    <div>
      <form onSubmit={submitForm} className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-1 bg-gray-100 rounded-[10px] p-1 w-fit">
            {tabs.map(({ key, label, Icon, hidden }) => {
              if (hidden) return null;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-[8px] text-[13px] font-medium ${
                    activeTab === key
                      ? "bg-white shadow-sm"
                      : "text-muted-foreground"
                  }`}
                >
                  <Icon size={14} /> {label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-4">
            {marketingCampaign && marketingCampaign.status === "draft" && (
              <DefaultModal
                modalButtonTriggerText={
                  <Button
                    design="primary-outline"
                    text={
                      marketingCampaign.scheduled_at
                        ? "Schedule Campaign"
                        : "Publish Campaign"
                    }
                    isLoading={api.postLoading}
                  />
                }
                onConfirmationAction={publish}
                description={`Are you sure you want to ${
                  marketingCampaign.scheduled_at ? "schedule" : "publish"
                } this campaign?`}
              />
            )}
            <Button
              text={marketingCampaign ? "Update Campaign" : "Create Campaign"}
              type="submit"
              isLoading={api.postLoading}
            />
          </div>
        </div>

        {activeTab === "edit" && (
          <div className="grid gap-4">
            <div className="grid md:grid-cols-2 gap-4">
              <InputField
                label="Campaign Name"
                value={campaignName}
                onChangeInput={(e) => setCampaignName(e.target.value)}
              />

              <InputField
                label="Subject"
                value={campaignSubject}
                onChangeInput={(e) => setCampaignSubject(e.target.value)}
                placeholder="Leave empty to use template subject"
              />

              <InputField
                label="Preheader (optional)"
                value={campaignPreheader}
                onChangeInput={(e) => setCampaignPreheader(e.target.value)}
                placeholder="Leave empty to use template subject"
              />

              <DatePickerField
                label="Schedule date (optional)"
                value={scheduledDate}
                onChangeInput={(value) => setScheduledDate(value)}
                clearable
              />

              <label>
                <span className="label">Schedule time (optional)</span>
                <input
                  type="time"
                  className="input mt-1"
                  value={scheduledTime || ""}
                  onChange={(e) => setScheduledTime(e.target.value)}
                />
              </label>
            </div>

            <TemplateEditor value={content} onChange={setContent} />

            <Button
              text={marketingCampaign ? "Update Campaign" : "Create Campaign"}
              type="submit"
              isLoading={api.postLoading}
            />
          </div>
        )}

        {activeTab === "preview" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              {campaignSubject ? (
                <div className="text-sm">
                  <span className="font-semibold">Subject:</span>{" "}
                  {campaignSubject}
                </div>
              ) : (
                <div className="text-[12px] text-muted-foreground">
                  Subject will be taken from the template if not provided.
                </div>
              )}
              <button
                type="button"
                onClick={() => setIsFullscreen(true)}
                disabled={!content}
                className="flex items-center gap-1.5 px-3 py-1.5 border rounded-[10px] text-[12.5px] font-medium text-muted-foreground disabled:opacity-40"
              >
                <Maximize2 size={13} /> Fullscreen
              </button>
            </div>

            <div className="border rounded-[15px] overflow-hidden bg-white">
              <iframe
                title="template-draft-preview"
                className="w-full h-[560px]"
                srcDoc={
                  content ||
                  "<p style='font-family:sans-serif;padding:16px;color:#9ca3af;'>Start building your template to see a preview.</p>"
                }
              />
            </div>
          </div>
        )}
      </form>

      {isFullscreen && (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 border-b">
            <div className="text-sm">
              {campaignSubject ? (
                <>
                  <span className="font-semibold">Subject:</span>{" "}
                  {campaignSubject}
                </>
              ) : (
                <span className="text-muted-foreground">Draft preview</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsFullscreen(false)}
              className="flex items-center gap-1.5 px-3 py-1.5 border rounded-[10px] text-[12.5px] font-medium text-muted-foreground"
            >
              <X size={13} /> Close
            </button>
          </div>
          <iframe
            title="template-draft-preview-fullscreen"
            className="w-full flex-1"
            srcDoc={content}
          />
        </div>
      )}

      {activeTab === "test" && (
        <div className="space-y-3 mt-5">
          <div className="grid md:grid-cols-3 gap-3 items-end">
            <InputField
              label="To email"
              value={testToEmail}
              onChangeInput={(e) => setTestToEmail(e.target.value)}
              placeholder="me@example.com"
            />
            <Button
              text="Send Test"
              onClick={() => sendTest()}
              isLoading={api.postLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingCampaignForm;
